<template>
  <v-select
    :items="events"
    :modelValue="selectedEvent ? modelValue : 'all'"
    @update:model-value="onSelectEvent"
    @click:clear="onSelectEvent('all')"
    item-title="name"
    item-value="id"
    hide-no-data
    hide-details
    density="compact"
    variant="outlined"
    :menu-props="{ contentClass: 'event-select-dropdown' }"
    :item-props="getItemProps"
    :class="{ 'event-select--pastel': hasPastelField }"
    :style="{ '--event-color': fieldBackgroundColor }"
  >
    <template #clear="{ props }">
      <span v-bind="props" class="d-flex" :class="{ 'hidden': modelValue === 'all' }">
        <v-tooltip location="right" open-delay="100">
          <template v-slot:activator="{ props: tooltipProps }">
            <v-icon v-bind="tooltipProps" size="small" icon="mdi-close-circle" color="#777" />
          </template>
          <span>Clear selection</span>
        </v-tooltip>
      </span>
    </template>
    <template v-slot:item="{ props, item }">
      <v-list-item v-bind="props">
        <v-icon v-if="modelValue === item.value" color="#777" icon="mdi-check" />
      </v-list-item>
    </template>
  </v-select>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import useEventActions from '@/composables/useEventActions'
import { lightenColor, getDarkThemeColor } from '@/utils/color'
import { isDarkTheme } from '@/composables/useAppTheme'

const { eventId } = defineProps({ eventId: String })

const modelValue = ref(eventId || 'all')
const store = useStore()
const { changeSelectedEvent } = useEventActions()

const workflow = computed(() => store.state.workflow.workflow)
const events = computed(() => {
  const workflowEventColor = workflow.value.eventDefinition?.color?.rgb || 'rgb(255, 255, 255)'

  const workflowEvents = (workflow.value.domainEvents ?? []).map((event) => ({
    id: event.id,
    name: event.description,
    color: event.color?.rgb || workflowEventColor
  }))

  return [{ id: 'all', name: 'All events', color: 'rgb(255, 255, 255)' }, ...workflowEvents]
})
const selectedEvent = computed(() => {
  return events.value.find((event) => event.id === modelValue.value)
})

const fieldBackgroundColor = computed(() => {
  if (isDarkTheme()) {
    if (!selectedEvent.value || selectedEvent.value.id === 'all') {
      return 'transparent'
    }
    return getDarkThemeColor(selectedEvent.value.color)
  }
  return selectedEvent.value ? lightenColor(selectedEvent.value.color) : 'rgb(255, 255, 255)'
})

const hasPastelField = computed(() => {
  return isDarkTheme() && !!selectedEvent.value && selectedEvent.value.id !== 'all'
})

const onSelectEvent = (eventId) => {
  modelValue.value = eventId
  changeSelectedEvent(eventId === 'all' ? null : eventId)
}

const getItemProps = (item) => {
  if (isDarkTheme()) {
    if (item.id === 'all') {
      return {}
    }
    return {
      class: 'event-select-item-dark',
      style: `background-color: ${getDarkThemeColor(item.color || 'rgb(255, 255, 255)')} !important`
    }
  }
  return {
    style: `background-color: ${lightenColor(item.color || 'rgb(255, 255, 255)')} !important`
  }
}

watch(() => eventId, (newEventId) => {
  if (modelValue.value !== newEventId) {
    modelValue.value = newEventId || 'all'
  }
})
</script>

<style scoped>
.v-select {
  & ::v-deep(.v-field) {
    background-color: var(--event-color);

    &.v-field--focused {
      .v-field__outline {
        --v-field-border-width: 1px;
      }
    }

    .v-field__field {
      .v-field__input {
        min-height: 36px;
      }
    }
  }
}

.v-select.event-select--pastel {
  & ::v-deep(.v-field__input),
  & ::v-deep(.v-select__selection-text) {
    color: #000000;
  }

  & ::v-deep(.v-select__menu-icon) {
    color: #000000;
    opacity: 1;
  }
}
</style>

<style>
.v-select {
  .v-field {
    .v-field__clearable {
      opacity: 1;

      .hidden {
        visibility: hidden;
      }
    }
  }
}

.event-select-dropdown {
  && .v-list {
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 10px;
      background-color: #aaa;
      background-origin: content-box;
    }

    .v-list-item {
      min-height: 40px;

      &.v-list-item--active {
        .v-list-item__overlay {
          opacity: 0;
        }
      }

      .v-list-item__content {
        display: flex;
        justify-content: space-between;
      }
    }
  }
}
</style>
